import { Loader2 } from 'lucide-react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getRegistration, resendRegistrationVerification, startRegistrationCheckout } from '../../api/registrationApi'
import {
  cardFeatures,
  cardTiers,
  formatPrice,
  introLine,
  introLimitLine,
  introMaxPerBuyer,
  isTrialPackage,
  limitLines,
  recommendedTier
} from '../../data/catalog'
import { PLAN_CARD_CLASS, PlanGrid } from '../billing/PlanGrid'
import { PriceBlock } from '../billing/PriceBlock'
import { useCatalog } from '../../store/CatalogStore'
import { getFriendlyErrorMessage } from '../../utils/errorMessage'
import { validations } from '../../utils/utils'
import { ErrorBox, InfoBox, RegistrationShell, fieldClass, linkButtonClass, primaryButtonClass } from './RegistrationShell'

const CHECK = (
  <svg
    className='h-4 w-4 shrink-0 text-emerald-300'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    aria-hidden='true'
  >
    <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
  </svg>
)

// The Free Trial package is a one-time purchase that lasts `durationDays`; every other card is a plan.
const startLabel = (tier) => (isTrialPackage(tier) ? `Start ${tier.durationDays}-day trial · ${formatPrice(tier.priceCents)}` : 'Subscribe')

/**
 * The plans the sign-up picker shows cards for: every monthly plan on sale, one card each, side by side.
 * The Free Trial package is retired (every plan has its own intro period), so a TRIAL row is never a card.
 */
export const pickerTiers = (catalog) => cardTiers(catalog)

/**
 * Display order: the recommended plan sits in the middle, so it reads as the default choice; the
 * others keep their rank order around it (Free Trial · AI Complete · AI Report).
 */
export const centerRecommended = (plans) => {
  const recommended = plans.find((t) => t.recommended)
  if (!recommended || plans.length < 3) return plans
  const rest = plans.filter((t) => t !== recommended)
  const middle = Math.floor(plans.length / 2)
  return [...rest.slice(0, middle), recommended, ...rest.slice(middle)]
}

/** A sign-up plan card; every card renders the same slots so they line up across the row (see PLAN_CARD_CLASS). */
export const PlanCard = ({ tier, features, pending, onStart, promoDays }) => {
  const limits = limitLines(tier)
  return (
    <div
      className={`relative rounded-2xl p-6 ${PLAN_CARD_CLASS} ${
        tier.recommended
          ? 'border-2 border-[#3B82F6] bg-white/[0.09] shadow-2xl shadow-blue-500/20'
          : 'border border-white/[0.12] bg-white/[0.04]'
      }`}
    >
      {tier.recommended && (
        <span className='absolute -top-3 left-6 rounded-md bg-[#1F6FEB] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white'>
          Recommended
        </span>
      )}
      {/* Every slot below is one shared row (see PLAN_CARD_CLASS): always rendered, even when empty. */}
      <div className='text-[18px] font-bold text-white'>{tier.name}</div>
      <div className='mt-1 text-[13px] text-white/60'>{tier.tagline}</div>
      <div className='mt-3'>
        <PriceBlock tier={tier} tone='dark' />
        {promoDays ? (
          <div className='mt-2 text-[13px] font-semibold text-[#4ADE80]'>
            Free for {promoDays} days with your promo code, then {formatPrice(tier.priceCents)}/mo
          </div>
        ) : null}
      </div>
      <ul className='mt-4 space-y-1.5' aria-label={features.heading || 'Features'}>
        {features.heading && (
          <li className='mb-1 border-b border-white/10 pb-2 text-[13px] font-semibold text-white/60'>{features.heading}</li>
        )}
        {features.items.map((label) => (
          <li key={label} className='flex items-center gap-2 text-[14px] text-white/85'>
            {CHECK}
            {label}
          </li>
        ))}
      </ul>
      <ul className={limits.length > 0 ? 'mt-3 space-y-1 border-t border-white/10 pt-3' : ''} aria-label='Plan limits'>
        {limits.map((label) => (
          <li key={label} className='text-[12px] text-white/70'>
            · {label}
          </li>
        ))}
      </ul>
      <div className={tier.perks.length > 0 ? 'mt-3 border-t border-white/10 pt-3' : ''}>
        {tier.perks.map((label) => (
          <div key={label} className='text-[12px] text-white/50'>
            · {label}
          </div>
        ))}
      </div>
      <button
        type='button'
        onClick={() => onStart(tier.id)}
        disabled={pending}
        className={`mt-6 flex h-11 w-full items-center justify-center rounded-full text-[13px] font-bold uppercase tracking-widest transition-all disabled:pointer-events-none disabled:opacity-60 ${
          tier.recommended
            ? 'bg-gradient-to-r from-[#1F6FEB] to-[#1F6FEB] text-white shadow-lg shadow-blue-500/25 hover:brightness-110'
            : 'border border-white/25 bg-white/[0.06] text-white hover:bg-white/[0.12]'
        }`}
      >
        {pending ? <Loader2 className='h-5 w-5 animate-spin' /> : promoDays ? `Start ${promoDays} days free` : startLabel(tier)}
      </button>
    </div>
  )
}
PlanCard.propTypes = {
  tier: PropTypes.object.isRequired,
  /** The features section: `{ heading, items }` (cardFeatures). */
  features: PropTypes.shape({ heading: PropTypes.string, items: PropTypes.arrayOf(PropTypes.string) }).isRequired,
  pending: PropTypes.bool,
  onStart: PropTypes.func.isRequired,
  /** A promo code covers this plan: free for this many days (shown on the card and the button). */
  promoDays: PropTypes.number
}

/**
 * /register/plan?token=… — the plan picker shown once the email is confirmed: the Free Trial
 * package ($1 one-time, 30 days of AI Complete) and the monthly plans. Each opens Stripe Checkout
 * (a one-time payment for the trial, a subscription for a plan); the browser comes back to
 * /register/complete. ?checkout=cancel means Stripe sent them back without paying.
 */
export const ChoosePlan = () => {
  const history = useHistory()
  const query = qs.parse(window.location.search)
  const token = query?.token
  const cancelled = query?.checkout === 'cancel'
  const [reg, setReg] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [pending, setPending] = useState(false)
  const [actionError, setActionError] = useState(null)
  // Plans, prices and copy come from the plan catalog in the database, not from this file.
  const { catalog, status: catalogStatus } = useCatalog()
  const plans = pickerTiers(catalog)
  const [resendEmail, setResendEmail] = useState('')
  const [resendEmailError, setResendEmailError] = useState(false)
  const [resent, setResent] = useState(false)

  const onResend = async (event) => {
    event.preventDefault()
    if (!validations.email(resendEmail)) {
      setResendEmailError(true)
      return
    }
    setResendEmailError(false)
    try {
      await resendRegistrationVerification(resendEmail.trim().toLowerCase())
    } catch (error) {
      // Treated as sent either way; see registrationApi.
    }
    setResent(true)
  }

  useEffect(() => {
    if (!token) {
      setLoadError({ status: 404, message: 'This link is not valid.' })
      return
    }
    let active = true
    getRegistration(token)
      .then((view) => active && setReg(view))
      .catch((error) => active && setLoadError({ status: error?.response?.status, message: getFriendlyErrorMessage(error) }))
    return () => {
      active = false
    }
  }, [token])

  const onStart = async (tierKey) => {
    setPending(true)
    setActionError(null)
    try {
      const result = await startRegistrationCheckout(token, tierKey)
      if (!result?.url) throw new Error('Checkout did not return a URL.')
      window.location.assign(result.url)
    } catch (error) {
      setPending(false)
      setActionError({ status: error?.response?.status, message: getFriendlyErrorMessage(error) })
    }
  }

  if (loadError) {
    return (
      <RegistrationShell width='narrow' title={loadError.status === 410 ? 'This link has expired' : 'This link isn’t valid'}>
        <ErrorBox>{loadError.message}</ErrorBox>
        {resent ? (
          <div className='mt-4'>
            <InfoBox>If that address has a pending sign-up, a new link is on its way.</InfoBox>
          </div>
        ) : (
          <form onSubmit={onResend} noValidate className='mt-4 space-y-3'>
            <input
              type='email'
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              placeholder='Email you signed up with'
              className={`${fieldClass} ${resendEmailError ? 'border-red-400/60' : ''}`}
              aria-label='Email you signed up with'
            />
            {resendEmailError && <div className='pl-1 text-[11px] text-red-300'>Enter a valid email address</div>}
            <button type='submit' className={primaryButtonClass}>
              Send a new link
            </button>
          </form>
        )}
        <div className='mt-4 flex flex-col items-center gap-2'>
          <button type='button' onClick={() => history.push('/register')} className={linkButtonClass}>
            Start a new sign-up
          </button>
          <button type='button' onClick={() => history.push('/authenticate')} className={linkButtonClass}>
            Back to sign in
          </button>
        </div>
      </RegistrationShell>
    )
  }

  if (catalogStatus === 'error') {
    return (
      <RegistrationShell width='narrow' title='We couldn’t load the plans'>
        <ErrorBox>Check your connection and reload the page.</ErrorBox>
      </RegistrationShell>
    )
  }

  if (!reg || catalogStatus === 'idle' || catalogStatus === 'loading') {
    return (
      <RegistrationShell width='narrow' title='Loading your sign-up…'>
        <div className='flex justify-center py-6' aria-busy='true'>
          <Loader2 className='h-6 w-6 animate-spin text-white/70' />
        </div>
      </RegistrationShell>
    )
  }

  if (reg.status === 'PROVISIONED') {
    return (
      <RegistrationShell width='narrow' title='You’re all set'>
        <InfoBox>{reg.company_name} is already set up. Sign in to continue.</InfoBox>
        <button type='button' onClick={() => history.push('/authenticate')} className={`${linkButtonClass} mt-4 block w-full text-center`}>
          Sign in
        </button>
      </RegistrationShell>
    )
  }

  if (reg.status === 'PENDING_EMAIL_VERIFICATION') {
    return (
      <RegistrationShell width='narrow' title='Confirm your email first'>
        <InfoBox>Open the confirmation link we sent to {reg.owner_email} to choose a plan.</InfoBox>
      </RegistrationShell>
    )
  }

  // Every plan carries its own intro period ("$1 for the first 30 days"); the recommended plan's leads the copy.
  const lead = introLine(recommendedTier(catalog))
  const introLimit = introLimitLine(recommendedTier(catalog), introMaxPerBuyer(catalog))
  return (
    <RegistrationShell
      width='plans'
      title={`Choose a plan for ${reg.company_name}`}
      subtitle={
        lead
          ? `${lead}, then the plan's monthly price — manage or cancel any time in Settings → Billing.`
          : 'Pick a plan to subscribe. Your first month is billed today — manage or cancel any time in Settings → Billing.'
      }
    >
      {cancelled && !actionError && (
        <div className='mb-5'>
          <InfoBox>Checkout was cancelled — nothing was charged. Pick a plan whenever you&apos;re ready.</InfoBox>
        </div>
      )}
      {actionError && (
        <div className='mb-5'>
          <ErrorBox>
            {actionError.message}
            {actionError.status === 409 && (
              <>
                {' '}
                <button type='button' onClick={() => history.push('/authenticate')} className='underline'>
                  Sign in
                </button>
              </>
            )}
          </ErrorBox>
        </div>
      )}
      {introLimit && (
        <div className='mb-5'>
          <InfoBox>{introLimit}</InfoBox>
        </div>
      )}
      <PlanGrid count={plans.length} className='md:py-4'>
        {centerRecommended(plans).map((tier) => (
          <PlanCard key={tier.id} tier={tier} features={cardFeatures(tier)} pending={pending} onStart={onStart} />
        ))}
      </PlanGrid>
      <p className='mt-5 text-center text-[12px] text-white/45'>Payments are processed securely by Stripe. Prices in USD.</p>
    </RegistrationShell>
  )
}
